// Third-party
import Head from 'next/head';

// First-party
import { Slot, createPage } from '@lib/util/templateHelpers';
import { gameToLdJson } from '@lib/util/gameToLdJson';

// Component lib
import shared from '@lib/components';

// Local
import gameApi from '@/apis/drupal/game';
import GameTileLayout from '@/layouts/GameTileLayout';

// Local components
import AllGamesBlock from '@/components/AllGamesBlock';

const {Box, Block, Title, RouterLink, DocumentTitle, Content} = shared;

export async function getServerSideProps () {
  const games = await gameApi.listFeaturedOrSticky(process.env.APP_CONFIG.DEFAULT_GAME_LIMIT);
  
  return {
    props: {
      games
    },
  };
}

export default createPage('FeaturedPage', {}, function FeaturedPage ({ className }, props) {
  return (
    <GameTileLayout className={className}>
      <DocumentTitle title='Featured IO Games' />

      <Head>
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'WebSite',
              'name': process.env.APP_CONFIG.DEFAULT_TITLE,
              'url': process.env.APP_CONFIG.URL + '/featured'
            }, null, 2)
          }}
        />
        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Thing", 
                    "@id": process.env.APP_CONFIG.URL,
                    "name": "Games"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Thing", 
                    "@id":process.env.APP_CONFIG.URL + '/featured',
                    "name": "Featured io Games"
                  }
                }
              ],
            }, null, 2)
          }}
        />

        <script 
          type='application/ld+json' 
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify({
              '@type': 'ItemList',
              'itemListElement': props.games.map((game, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'url': process.env.APP_CONFIG.URL + game.path,
                'name': game.name
              //  'item': gameToLdJson(game)
              }))
            }, null, 2)
          }}
        /> 
      </Head>

      <AllGamesBlock feature games={props.games} fetchMoreGames={gameApi.listFeaturedOrSticky}>
        <Slot name='title'>Featured io Games</Slot>
      </AllGamesBlock>
      {
        <Block>
          <Box className='IndexPage__BottomBox'>
            <Title>Featured IO Games</Title>
            <Content>
              <Title h2 size4>Handpicked Selections from the IO Gaming Universe</Title>

              <p>Welcome to our Featured Games section, where we spotlight exceptional io games worthy of your attention! At <RouterLink href="/">iogames.space</RouterLink>, our editorial team carefully selects titles that stand out for various reasons – whether they're breaking new ground, seeing a surge in popularity, receiving significant updates, or simply delivering an outstanding gaming experience. From time to time, our featured lineup includes gems like <RouterLink href="/football-bros">Football Bros</RouterLink>, <RouterLink href="/hexanautio">Hexanaut.io</RouterLink>, <RouterLink href="/smash-karts-io-game">Smash Karts</RouterLink>, and <RouterLink href="/starblast-io">Starblast.io</RouterLink>.</p>
              
                <Title h2 size4>What Makes a Game Featured?</Title>
                <p>Featured games earn their spotlight for different reasons, making this collection a diverse showcase of what makes io gaming special. Games may be featured because they:</p>
                <ul>
                  <li>Recently launched with innovative gameplay mechanics</li>
                  <li>Are trending with rapidly growing player communities</li>
                  <li>Maintain consistent popularity over extended periods</li>
                  <li>Received substantial updates that transformed the experience</li>
                  <li>Represent the highest quality in their respective genres</li>
                </ul>

                <Title h2 size4>Fresh Updates and Innovation</Title>
                <p><RouterLink href="/starblast-io">Starblast.io</RouterLink> earned its featured status with a major content update that introduced new ship classes and gameplay modes, breathing fresh life into this space combat classic. Meanwhile, <RouterLink href="/hexanautio">Hexanaut.io</RouterLink> continues to refine its strategic territorial gameplay with regular balance adjustments and community-inspired features.</p>
                
                <Title h2 size4>Rising Stars and Established Favorites</Title>
                <p>Some games earn their featured spot through sheer quality and staying power. <RouterLink href="/football-bros">Football Bros</RouterLink> delivers consistent multiplayer sports action that keeps players coming back season after season. For adrenaline-pumping mayhem, <RouterLink href="/smash-karts-io-game">Smash Karts</RouterLink> remains a go-to choice with its perfectly tuned blend of racing and combat mechanics that's impossible to put down.</p>
                
                <Title h2 size4>Our Editorial Process</Title>
                <p>Our team regularly reviews the io gaming landscape to update our featured selections. We consider player feedback, developer communication, gameplay innovation, and community growth when determining which games deserve the spotlight. This curated approach ensures you'll find something truly special whenever you explore our featured collection.</p>
                
                <p>We refresh our featured games regularly, so check back often to discover titles that represent the very best the io gaming world has to offer. Have you played a game that deserves to be featured? <RouterLink href="/contact-us">Share your recommendations</RouterLink> with us!</p>
              </Content>
            </Box>
          </Block>
        }
    </GameTileLayout>
  );
});
